import type { GenTVCreationCommandV2, GenTVCreationReplyV2, GenTVPublicCreationV2, GenTVPrivateCreationV2 } from './GenTVV2';
import type { GenTVRuntimeCommandV2, GenTVPublicRuntimeV2, GenTVPrivateRuntimeV2 } from './GenTVRuntimeV2';
/** Wire contracts. Private state is delivered only to its authenticated owner. */
export type GenTVBuiltInScenarioId = 'space-outlaw-rescue' | 'mountain-lodge-mystery' | 'midnight-meridian' | 'sunken-signal' | 'glass-crown' | 'starlight-opening';
export type GenTVScenarioId = GenTVBuiltInScenarioId | `custom:${string}`;
export interface GenTVStoryContribution { id: string; text: string; authorLabel: string }
export interface GenTVStoryPitch { title: string; genre: string; premise: string; audienceFeeling?: string; contributions?: GenTVStoryContribution[] }
export interface GenTVStoryPermissions { episodeId: string; ownedContributionIds: string[] }
export interface GenTVStoryIdea extends GenTVStoryPitch { id: string; revision?: string }
export type GenTVPhase = 'lobby' | 'briefing' | 'resolving' | 'generating' | 'playback' | 'finished';
export type GenTVVisibility = 'public' | 'covert' | 'reveal_only_if_successful';

/**
 * One of the two ways a role can be cast (#4247). Every story has four roles and a room may be any four
 * people, so each role is playable as a woman or as a man and the player picks which they are.
 */
export interface GenTVPresentation {
  as: 'woman' | 'man';
  name: string;
  appearance: string;
}

export interface GenTVCharacter {
  id: string;
  name: string;
  role: string;
  /**
   * The role's declared look. `presentations` is what a scene is actually filmed with once a player has
   * chosen; this stays as authored because it is what the pre-rendered environments on the CDN were
   * matched against, and moving it would cost every story its world (#4248).
   */
  appearance: string;
  /** Both ways this role can be cast. The first is the authored default, and carries the authored name. */
  presentations?: GenTVPresentation[];
  background: string;
  motivation: string;
  relationships: string;
  capabilities: string[];
}

export interface GenTVScenario {
  id: GenTVScenarioId;
  title: string;
  premise: string;
  promise?: string;
  question?: string;
  genre?: string;
  stakes?: string;
  pressureLabel: string;
  posterUrl: string;
  /** The scenario's rendered location still (#3976), set once the episode begins with an environment; the stage paints it in place of the poster. */
  stillUrl?: string;
  characters: GenTVCharacter[];
}

export interface GenTVAction {
  intendedAction: string;
  desiredOutcome: string;
  tactic: string;
  performance?: string;
  visibility: GenTVVisibility;
}

export interface GenTVSceneContract {
  /** The server is preparing the public direction; do not dispatch this draft to video. */
  preparing?: boolean;
  genreRules?: string[];
  props?: { taskId: string; description: string }[];
  voices?: Record<string, { traits: string[]; neverSays: string }>;
  template?: 'discovery' | 'confrontation' | 'attempt' | 'exposure' | 'button';
  cards?: { title: string; text: string }[];
  /** Public story context for the writer; the current shot actions still control video. */
  premise?: string;
  sceneId: string;
  number: number;
  /** The scene as the Director and the story writers read it: event directions and the ending. Players read `narration`. */
  summary: string;
  /**
   * The scene as players read it — text scenes, captions, receipts (#4206): each public event once, in plain prose,
   * with no direction for the video model. Absent on scenes composed without resolved moves (legacy, or before
   * #4206); readers fall back to `summary`.
   */
  narration?: string;
  location: string;
  featuredCharacters: GenTVCharacter[];
  camera: string;
  mood: string;
  durationSeconds: number;
  /** Ordered public consequences. Timing is an editing target, not a provider guarantee. */
  shots?: GenTVShot[];
  /** Established public facts that subsequent shots must preserve. */
  continuity?: { id: string; description: string; sceneNumber: number }[];
  resolution: '480p' | '768p';
  deliveryWidth: 1280;
  deliveryHeight: 720;
  finale: boolean;
}

export interface GenTVShot {
  /** One lobby-authored faceless still; encoded locally with a slow push. */
  framing?: 'wide' | 'single' | 'insert' | 'two-shot';
  stillPrompt?: string;
  card?: { title: string; text: string };
  /**
   * What this shot is, in the players' own words (#4236): the first sentence of the resolved event's `narration`
   * (#4801), burned over the picture so the film says who did what. One shot per event carries it.
   */
  caption?: string;
  id: string;
  /** Seconds an authored still holds. A generated take always plays whole (#4801), whatever this says. */
  durationSeconds: 2 | 3 | 5;
  characterIds: string[];
  action: string;
  dialogue: { speakerId: string; listenerId?: string; text: string }[];
  eventIds: string[];
}

export interface GenTVScene {
  contract: GenTVSceneContract;
  assetUrl: string | null;
  /** VP8/Opus copy for the server program browser, which may lack an H.264 decoder. */
  programAssetUrl?: string | null;
  posterUrl: string;
  playbackStartsAt: number;
  durationSeconds: number;
  fallback: boolean;
  /**
   * Why this scene has no video, recorded when it stopped (#4206), so the reason survives the feed's later states — a
   * finished episode's feed reads `ended` whatever stopped it. Safe public status only; provider errors and
   * credentials never enter room state. `capacity`: this server was already running another room's live video.
   */
  videoIssue?: 'budget' | 'generation' | 'delivery' | 'capacity';
}

export interface GenTVPublicState {
  /** Absent on legacy episodes; fixed at creation, independent of rollout switches. */
  rulesVersion?: 2;
  creationV2?: GenTVPublicCreationV2;
  runtimeV2?: GenTVPublicRuntimeV2;
  /** Absent on persisted legacy episodes, which retain their original rules. */
  moveMode?: 'curated';
  worldFacts?: { id: string; description: string; sceneNumber: number }[];
  storySimplified?: boolean;
  /** The scene has been watched (or went to text) and the next moves are still being written; the round opens when they land (#4207). */
  awaitingChoices?: boolean;
  /** One server-owned feed per room. Time is a session limit, not a billing quote. */
  liveVideo?: {
    streamId: string;
    status: 'idle' | 'starting' | 'connecting' | 'live' | 'paused' | 'ended' | 'failed' | 'budget';
    budgetSeconds: number;
    usedSeconds: number;
    promptVersion: number;
    /** The relay (media-origin) is still being placed; the feed will connect once it is. Not charged to the time limit. */
    relayWaiting?: boolean;
    /** Why a `failed` feed never started, when the server knows: `capacity` — this server was already running its one live video. */
    reason?: 'capacity';
  };
  /**
   * The narrator (#4803): a voice over the feed that says what just happened, whose turn it is and how it ended, in
   * plain words, over the silent holds between scenes. `voiceId` is the host's Inworld pick; null is the default voice.
   */
  narrator?: { voiceId: string | null };
  /** Lines the feed should say, oldest first; each `id` is spoken once. Two can land in one tick (the opening and the first turn). */
  narration?: { id: string; text: string; at: number }[];
  roomId: string;
  episodeId: string;
  scenario: GenTVScenario;
  phase: GenTVPhase;
  paused: boolean;
  sceneNumber: number;
  /** Episode length: the scene the clock runs out at for a table that completes nothing, not a fixed count (#4205). */
  sceneLimit: number;
  actionWindowSeconds: number;
  deadline: number | null;
  pressure: number;
  pressureLimit: number;
  progress: number;
  /** `preparing`: the player's costumed portrait is being made from their photo (#4805); Begin waits for it. */
  players: { characterId: string; claimId?: string; ready: boolean; appearance?: 'pending' | 'preparing' | 'photo' | 'authored' }[];
  participantCharacters?: boolean;
  referenceGenerationUsed?: number;
  scene: GenTVScene | null;
  history: { sceneId: string; summary: string }[];
  generationUsed: number;
  generationBudget: number;
  serverTime: number;
  /** The finished episode, cut and saved. Every participant may download it until the host exits. */
  episode?: { seconds: number; bytes: number; savedAt: number };
  /** V2 captures the actual public canvas/audio bus; a missing recording never implies success. */
  recordingV2?: { status: 'recording' | 'saving' | 'saved' | 'unavailable'; interrupted: boolean;
    reason?: 'capture_lost' | 'missing_presentation' | 'duration_mismatch' | 'save_failed' | 'no_media' };
  previousRecordingV2?: NonNullable<GenTVPublicState['recordingV2']> & { episodeId: string; seconds?: number; bytes?: number; savedAt?: number };
  /** Shared lobby proposals contain no generated private character details. */
  storyIdeas?: GenTVStoryIdea[];
  storySource?: GenTVStoryIdea;
  storyBuilding?: boolean;
  storyApprovalPending?: boolean;
  storyBuildsUsed?: number;
  /**
   * V2 (#5136): set while a player's private briefing is still being written, on the server clock. Begin holds until
   * every briefing has landed or this time has passed, so nobody starts the show without hearing who they are.
   */
  privateBriefingsPendingUntil?: number;
}

export type GenTVVoiceCueKindV2 = 'briefing' | 'dilemma' | 'nudge' | 'anticipation' | 'result' | 'closing';
/**
 * One line of the private voice (#5028, spec 06 P5): written for one player and played only on their device.
 * `url` is an owner-authorized route, never a public object; `text` is the one-line caption, always shown.
 * `playAt` names the silent gap it belongs to: during `segmentId` (it is playing), or, with `afterLineId`, once that
 * segment's line has finished and before anything else airs. `lobby` cues play before Begin. A cue whose moment has
 * passed is dropped, never queued (P2), except `dilemma`, which may play while its decision window is open.
 */
export interface GenTVVoiceCueV2 {
  cueId: string;
  kind: GenTVVoiceCueKindV2;
  /** Absent when synthesis failed: the caption still shows. */
  url?: string;
  durationMs: number;
  text: string;
  playAt: { segmentId: string; afterLineId?: string; lobby?: true;
    /** `nudge`: only once the window has at most this long left, and only while this player has not chosen. */
    remainingMsAtMost?: number; unchosenOnly?: true };
}

export interface GenTVPrivateState {
  creationV2?: GenTVPrivateCreationV2;
  runtimeV2?: GenTVPrivateRuntimeV2;
  /** Owner-only private voice (#5028): current and future cues only. Never in public state, the program or the recording. */
  voiceCues?: GenTVVoiceCueV2[];
  /** Owner-only, replaceable episode feedback. Never included in public projections or the recording bus. */
  episodeFeedbackV2?: { rating: number };
  roomId: string;
  episodeId: string;
  characterId: string;
  /** Changes when a role is reclaimed so stale private updates cannot restore an old claim. */
  claimId?: string;
  secret: string;
  objective: string;
  arc: string;
  /** Which of the role's presentations this player chose to play it as (#4247). */
  presentation?: 'woman' | 'man';
  briefing: string;
  submitted: boolean;
  sceneNumber?: number;
  /** Scene-specific options; `consequence` says how the move steers the story. */
  suggestedMoves?: { dramaticFunction?: 'advance' | 'relationship' | 'gamble'; id: string; label: string; consequence?: string; action: GenTVAction }[];
  lastMove?: {
    sceneNumber: number;
    text: string;
    /** Private resolution feedback for the player who submitted the move. */
    status?: 'submitted' | 'accepted' | 'modified' | 'rejected' | 'held';
    guidance?: string;
    outcomeSceneNumber?: number;
    eventIds?: string[];
  };
  appearance?: 'pending' | 'preparing' | 'photo' | 'authored';
  /** Owner-only camera preview. Never sent to observers or the public stage. */
  referenceImage?: string;
  messages: { role: 'player' | 'gm'; text: string }[];
}

export type GenTVCommand =
  | GenTVRuntimeCommandV2
  | GenTVCreationCommandV2
  | { type: 'select_scenario'; scenarioId: GenTVScenarioId; actionWindowSeconds?: number; generationBudget?: number; videoSeconds?: number; liveVideo?: boolean; participantCharacters?: boolean; resetCast?: boolean; episodeId?: string; narratorVoiceId?: string | null }
  | { type: 'choose_character'; characterId: string; episodeId?: string; presentation?: 'woman' | 'man' }
  | { type: 'release_character'; episodeId: string; characterId: string; claimId?: string }
  | { type: 'set_character_reference'; episodeId: string; characterId: string; claimId?: string; image: string | null }
  | { type: 'suggest_story'; pitch: GenTVStoryPitch }
  | { type: 'contribute_story'; episodeId: string; ideaId: string; expectedRevision: string; requestId: string; text: string }
  | { type: 'undo_story_contribution'; episodeId: string; ideaId: string; contributionId: string }
  | { type: 'build_story'; ideaId: string; expectedRevision?: string }
  | { type: 'replay_episode'; episodeId: string; sequel?: boolean }
  | { type: 'approve_story'; episodeId: string; scenarioId: GenTVScenarioId }
  | { type: 'start' | 'pause' | 'resume' | 'skip' | 'end' }
  | { type: 'action'; sceneNumber: number; action: GenTVAction }
  | { type: 'suggested_action'; sceneNumber: number; moveId: string }
  | { type: 'chat'; message: string };

export interface GenTVReply { ok: boolean; error?: string; code?: string; episodeId?: string; creationReply?: GenTVCreationReplyV2; capabilities?: { dynamicStoriesV2: boolean; seriesV2: boolean; supportedRulesVersions: readonly number[] }; storyPermissions?: GenTVStoryPermissions }
export interface GenTVVoiceRequest {
  episodeId: string;
  sceneNumber: number;
  audio: string;
  mimeType: 'audio/webm' | 'audio/mp4' | 'audio/ogg';
}
export interface GenTVVoiceReply extends GenTVReply { text?: string }

/**
 * Floor for a character reference image, on both edges. The clip provider rejects anything smaller
 * with HTTP 422 `image_too_small`, and a shot carrying one fails outright rather than degrading, so
 * the camera capture, the command boundary and the provider client all measure against this. #4005
 */
export const GENTV_REFERENCE_MIN_EDGE = 256;

/**
 * Scenes a curated episode may run past its Episode length (`sceneLimit`). Rounds that complete an objective
 * slow the clock, so a productive table plays on; the server finishes the episode by `sceneLimit` plus this,
 * sizes the live video limit to it, and the lobby tells the host. #4205
 */
export const GENTV_SCENE_OVERTIME = 2;

/** A mode-owned compositor input, separate from the room's final recording output. */
export const getGenTVProgramStreamPath = (roomId: string): string => `room-stage/${roomId}/gentv`;
export const isGenTVProgramStreamPath = (path: string): boolean =>
  /^room-stage\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/gentv$/i.test(path);

/** Raw public Director feed, distinct from the captioned program/compositor output. */
export const getGenTVLiveStreamPath = (roomId: string, streamId: string): string => `room-stage/${roomId}/gentv-live/${streamId}`;
export const isGenTVLiveStreamPath = (path: string): boolean =>
  /^room-stage\/[0-9a-f-]{36}\/gentv-live\/[0-9a-f-]{36}$/i.test(path);

/** The narrator's voice when the host has not picked one (#4803): an Inworld stock voice the room companions already use. */
export const GENTV_DEFAULT_NARRATOR_VOICE = 'Craig';
